import { Label } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { Slider } from '@creanizer/ui'
import { Checkbox } from '@creanizer/ui'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { generateEnemyContext, generateItemContext, generateCharacterContext } from '@creanizer/utils'
import type { DungeonInput } from '@creanizer/types'

interface DungeonFormDetailedProps {
  input: DungeonInput
  onChange: (input: DungeonInput) => void
  enemies: any[]
  items: any[]
  characters: any[]
  selectedEnemyId: string
  setSelectedEnemyId: (id: string) => void
  selectedItemId: string
  setSelectedItemId: (id: string) => void
  selectedCharacterId: string
  setSelectedCharacterId: (id: string) => void
}

export function DungeonFormDetailed({
  input,
  onChange,
  enemies,
  items,
  characters,
  selectedEnemyId,
  setSelectedEnemyId,
  selectedItemId,
  setSelectedItemId,
  selectedCharacterId,
  setSelectedCharacterId
}: DungeonFormDetailedProps) {
  return (
    <Tabs defaultValue="basic" className="w-full">
      <TabsList className="grid w-full grid-cols-5">
        <TabsTrigger value="basic">基本</TabsTrigger>
        <TabsTrigger value="structure">構造</TabsTrigger>
        <TabsTrigger value="environment">環境</TabsTrigger>
        <TabsTrigger value="contents">コンテンツ</TabsTrigger>
        <TabsTrigger value="story">ストーリー</TabsTrigger>
      </TabsList>
      
      <TabsContent value="basic" className="space-y-4">
        <div>
          <Label>ダンジョン名</Label>
          <Input
            value={input.name || ''}
            onChange={(e) => onChange({ ...input, name: e.target.value })}
            placeholder="例：忘れられた神殿、魔王の城"
          />
        </div>
        
        <div>
          <Label>ダンジョンタイプ</Label>
          <Input
            value={input.type || ''}
            onChange={(e) => onChange({ ...input, type: e.target.value })}
            placeholder="例：古代遺跡、洞窟、要塞、塔、地下墓地"
          />
        </div>
        
        <div>
          <Label>テーマ</Label>
          <Textarea
            value={input.theme || ''}
            onChange={(e) => onChange({ ...input, theme: e.target.value })}
            placeholder="ダンジョンの全体的なテーマや雰囲気"
            rows={3}
          />
        </div>
        
        <div>
          <Label>目的</Label>
          <Textarea
            value={input.purpose || ''}
            onChange={(e) => onChange({ ...input, purpose: e.target.value })}
            placeholder="このダンジョンの存在理由や目的"
            rows={2}
          />
        </div>
      </TabsContent>
      
      <TabsContent value="structure" className="space-y-4">
        <div>
          <Label>フロア数: {input.floors || 3}</Label>
          <Slider
            value={[input.floors || 3]}
            onValueChange={(value) => onChange({ ...input, floors: value[0] })}
            min={1}
            max={10}
            step={1}
          />
          <p className="text-xs text-muted-foreground mt-1">
            推奨: 1-5フロア（大規模なダンジョンは生成に時間がかかります）
          </p>
        </div>
        
        <div>
          <Label>部屋数（フロアあたり）: {input.roomsPerFloor || 5}</Label>
          <Slider
            value={[input.roomsPerFloor || 5]}
            onValueChange={(value) => onChange({ ...input, roomsPerFloor: value[0] })}
            min={1}
            max={50}
            step={1}
          />
        </div>
        
        <div>
          <Label>レイアウト</Label>
          <Select 
            value={input.layout} 
            onValueChange={(value) => onChange({ ...input, layout: value as DungeonInput['layout'] })}
          >
            <SelectTrigger>
              <SelectValue placeholder="レイアウトを選択" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="linear">直線的 - 一本道</SelectItem>
              <SelectItem value="branching">分岐 - 複数のルート</SelectItem>
              <SelectItem value="maze">迷路 - 複雑な構造</SelectItem>
              <SelectItem value="open">開放的 - 大部屋中心</SelectItem>
              <SelectItem value="custom">カスタム</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label>規模</Label>
          <Select 
            value={input.size} 
            onValueChange={(value) => onChange({ ...input, size: value as DungeonInput['size'] })}
          >
            <SelectTrigger>
              <SelectValue placeholder="規模を選択" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="small">小規模 - 1時間程度</SelectItem>
              <SelectItem value="medium">中規模 - 2-3時間</SelectItem>
              <SelectItem value="large">大規模 - 4-6時間</SelectItem>
              <SelectItem value="massive">超大規模 - 複数セッション</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </TabsContent>
      
      <TabsContent value="environment" className="space-y-4">
        <div>
          <Label>雰囲気</Label>
          <Textarea
            value={input.atmosphere || ''}
            onChange={(e) => onChange({ ...input, atmosphere: e.target.value })}
            placeholder="ダンジョンの雰囲気（不気味、神聖、廃墟、活気など）"
            rows={2}
          />
        </div>
        
        <div>
          <Label>環境</Label>
          <Textarea
            value={input.environment || ''}
            onChange={(e) => onChange({ ...input, environment: e.target.value })}
            placeholder="環境の詳細（温度、湿度、空気の質など）"
            rows={2}
          />
        </div>
        
        <div>
          <Label>光量</Label>
          <Select 
            value={input.lightLevel} 
            onValueChange={(value) => onChange({ ...input, lightLevel: value as DungeonInput['lightLevel'] })}
          >
            <SelectTrigger>
              <SelectValue placeholder="光量を選択" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="bright">明るい - 照明充実</SelectItem>
              <SelectItem value="dim">薄暗い - 部分的な照明</SelectItem>
              <SelectItem value="dark">暗い - ほぼ照明なし</SelectItem>
              <SelectItem value="varied">変化 - エリアによって異なる</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label>状態</Label>
          <Select 
            value={input.condition} 
            onValueChange={(value) => onChange({ ...input, condition: value as DungeonInput['condition'] })}
          >
            <SelectTrigger>
              <SelectValue placeholder="保存状態を選択" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pristine">完璧 - 新築同様</SelectItem>
              <SelectItem value="maintained">維持 - 定期的な手入れ</SelectItem>
              <SelectItem value="deteriorating">劣化 - 部分的な損傷</SelectItem>
              <SelectItem value="ruined">廃墟 - 大部分が崩壊</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </TabsContent>
      
      <TabsContent value="contents" className="space-y-4">
        <div>
          <Label>トラップ密度</Label>
          <Select 
            value={input.trapDensity} 
            onValueChange={(value) => onChange({ ...input, trapDensity: value as DungeonInput['trapDensity'] })}
          >
            <SelectTrigger>
              <SelectValue placeholder="トラップ密度を選択" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">なし - トラップ無し</SelectItem>
              <SelectItem value="low">低 - 重要地点のみ</SelectItem>
              <SelectItem value="medium">中 - 適度に配置</SelectItem>
              <SelectItem value="high">高 - 多数配置</SelectItem>
              <SelectItem value="extreme">極限 - 至る所に配置</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label>敵密度</Label>
          <Select 
            value={input.enemyDensity} 
            onValueChange={(value) => onChange({ ...input, enemyDensity: value as DungeonInput['enemyDensity'] })}
          >
            <SelectTrigger>
              <SelectValue placeholder="敵密度を選択" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">なし - 敵無し</SelectItem>
              <SelectItem value="low">低 - 少数配置</SelectItem>
              <SelectItem value="medium">中 - 適度に配置</SelectItem>
              <SelectItem value="high">高 - 多数配置</SelectItem>
              <SelectItem value="extreme">極限 - 常に戦闘</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label>宝物の豊富さ</Label>
          <Select 
            value={input.treasureRichness} 
            onValueChange={(value) => onChange({ ...input, treasureRichness: value as DungeonInput['treasureRichness'] })}
          >
            <SelectTrigger>
              <SelectValue placeholder="宝物の豊富さを選択" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="poor">貧弱 - ほぼ無し</SelectItem>
              <SelectItem value="modest">控えめ - 最小限</SelectItem>
              <SelectItem value="average">平均的 - 標準的</SelectItem>
              <SelectItem value="rich">豊富 - 多数配置</SelectItem>
              <SelectItem value="legendary">伝説的 - 宝の山</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label>パズル複雑度</Label>
          <Select 
            value={input.puzzleComplexity} 
            onValueChange={(value) => onChange({ ...input, puzzleComplexity: value as DungeonInput['puzzleComplexity'] })}
          >
            <SelectTrigger>
              <SelectValue placeholder="パズル複雑度を選択" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">なし - パズル無し</SelectItem>
              <SelectItem value="simple">簡単 - 単純な仕掛け</SelectItem>
              <SelectItem value="moderate">適度 - 考える必要あり</SelectItem>
              <SelectItem value="complex">複雑 - 高度な謎解き</SelectItem>
              <SelectItem value="genius">天才的 - 極めて困難</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {/* ジェネレータ間連携 */}
        {enemies.length > 0 && (
          <div className="space-y-2">
            <Label>配置エネミー</Label>
            <Select
              value={selectedEnemyId || 'none'}
              onValueChange={(value) => {
                if (value === 'none') {
                  setSelectedEnemyId('')
                  onChange({ 
                    ...input, 
                    relatedEnemyContext: undefined,
                    enemyRole: undefined 
                  })
                } else {
                  const enemy = enemies.find(e => e.id === value)
                  if (enemy) {
                    const context = generateEnemyContext(enemy)
                    onChange({ 
                      ...input, 
                      relatedEnemyContext: context 
                    })
                    setSelectedEnemyId(value)
                  }
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="エネミーを選択..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">なし</SelectItem>
                {enemies.map((enemy) => (
                  <SelectItem key={enemy.id} value={enemy.id}>
                    {enemy.result.basicInfo?.name || enemy.result.name || 'Unknown'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {selectedEnemyId && (
              <Input
                value={input.enemyRole || ''}
                onChange={(e) => onChange({ 
                  ...input, 
                  enemyRole: e.target.value 
                })}
                placeholder="役割：ボス、中ボス、雑魚、守護者等"
              />
            )}
          </div>
        )}
        
        {items.length > 0 && (
          <div className="space-y-2">
            <Label>配置アイテム</Label>
            <Select
              value={selectedItemId || 'none'}
              onValueChange={(value) => {
                if (value === 'none') {
                  setSelectedItemId('')
                  onChange({ 
                    ...input, 
                    relatedItemContext: undefined,
                    itemPlacement: undefined 
                  })
                } else {
                  const item = items.find(i => i.id === value)
                  if (item) {
                    const context = generateItemContext(item)
                    onChange({ 
                      ...input, 
                      relatedItemContext: context 
                    })
                    setSelectedItemId(value)
                  }
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="アイテムを選択..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">なし</SelectItem>
                {items.map((item) => (
                  <SelectItem key={item.id} value={item.id}>
                    {item.result.basicInfo?.name || item.result.name || 'Unknown'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {selectedItemId && (
              <Input
                value={input.itemPlacement || ''}
                onChange={(e) => onChange({ 
                  ...input, 
                  itemPlacement: e.target.value 
                })}
                placeholder="配置：宝箱、隠し部屋、ボスドロップ等"
              />
            )}
          </div>
        )}
      </TabsContent>
      
      <TabsContent value="story" className="space-y-4">
        <div>
          <Label>歴史</Label>
          <Textarea
            value={input.history || ''}
            onChange={(e) => onChange({ ...input, history: e.target.value })}
            placeholder="ダンジョンの歴史や建造の経緯"
            rows={3}
          />
        </div>
        
        <div>
          <Label>現在の住人</Label>
          <Input
            value={input.currentInhabitants || ''}
            onChange={(e) => onChange({ ...input, currentInhabitants: e.target.value })}
            placeholder="現在ダンジョンに住んでいる者たち"
          />
        </div>
        
        <div className="flex items-center space-x-2">
          <Checkbox
            id="secrets"
            checked={input.secrets || false}
            onCheckedChange={(checked) => onChange({ ...input, secrets: checked as boolean })}
          />
          <Label htmlFor="secrets">秘密を含む</Label>
        </div>
        
        {characters.length > 0 && (
          <div className="space-y-2">
            <Label>関連キャラクター</Label>
            <Select
              value={selectedCharacterId || 'none'}
              onValueChange={(value) => {
                if (value === 'none') {
                  setSelectedCharacterId('')
                  onChange({ 
                    ...input, 
                    relatedCharacterContext: undefined,
                    characterRole: undefined 
                  })
                } else {
                  const character = characters.find(c => c.id === value)
                  if (character) {
                    const context = generateCharacterContext(character)
                    onChange({ 
                      ...input, 
                      relatedCharacterContext: context 
                    })
                    setSelectedCharacterId(value)
                  }
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="キャラクターを選択..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">なし</SelectItem>
                {characters.map((character) => (
                  <SelectItem key={character.id} value={character.id}>
                    {character.result.name} ({character.result.role})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {selectedCharacterId && (
              <Input
                value={input.characterRole || ''}
                onChange={(e) => onChange({ 
                  ...input, 
                  characterRole: e.target.value 
                })}
                placeholder="役割：探索者、案内人、囚われの人等"
              />
            )}
          </div>
        )}
      </TabsContent>
    </Tabs>
  )
}